import type { Todo, TodoCalendar } from '@/api'
import { matchText } from './fuzzy'

export interface CategoryCalendar {
  id: number
  name: string
  count: number
}

export interface Category {
  name: string
  total: number
  calendars: CategoryCalendar[]
}

// Every tag used by a todo of the loaded calendars, with how often per calendar. Case variants count as one entry, shown the way
// they were most often written; `hidden` (lower case names, the built-in tags) is left out. Most used first, ties alphabetically.
export function collectCategories(
  todosByCalendar: Record<number, Todo[]>,
  calendars: TodoCalendar[],
  hidden: Set<string> = new Set(),
): Category[] {
  const entries = new Map<string, { spellings: Map<string, number>; perCalendar: Map<number, number> }>()
  for (const calendar of calendars) {
    for (const todo of todosByCalendar[calendar.id] ?? []) {
      for (const tag of new Set(todo.categories)) {
        const key = tag.toLowerCase()
        if (hidden.has(key)) continue
        const entry = entries.get(key) ?? { spellings: new Map(), perCalendar: new Map() }
        entry.spellings.set(tag, (entry.spellings.get(tag) ?? 0) + 1)
        entry.perCalendar.set(calendar.id, (entry.perCalendar.get(calendar.id) ?? 0) + 1)
        entries.set(key, entry)
      }
    }
  }
  const result: Category[] = []
  for (const entry of entries.values()) {
    const [name] = [...entry.spellings.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]
    const perCalendar = calendars
      .filter((calendar) => entry.perCalendar.has(calendar.id))
      .map((calendar) => ({ id: calendar.id, name: calendar.name, count: entry.perCalendar.get(calendar.id)! }))
    result.push({ name, total: perCalendar.reduce((sum, c) => sum + c.count, 0), calendars: perCalendar })
  }
  return result.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
}

// The categories a search keeps, best match first (prefix, then anywhere, then letters in order); an empty search keeps all.
export function searchCategories(categories: Category[], query: string): Category[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return categories
  const ranked: { category: Category; tier: number; index: number }[] = []
  categories.forEach((category, index) => {
    const match = matchText(category.name, needle)
    if (match) ranked.push({ category, tier: match.tier, index })
  })
  return ranked.sort((a, b) => a.tier - b.tier || a.index - b.index).map((r) => r.category)
}
